import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCheck, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDeData } from '@/componentes/CampoDeData';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { MaisAcoes } from '@/componentes/MaisAcoes';
import { RodapeDeContagem, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { formatarDia } from '@/lib/formato';
import { EXEMPLO_DO_MOTIVO, faltaNoMotivo } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { ehFichaAlterada } from '@/lib/versao-da-ficha';

import { apiDoFinanceiro, type ClassificacaoDoPlano, type ContaDoPlano, type DescricaoDaOrigem, type Grupo, type Origem, type Plano } from './api';
import {
  corpoDaConta,
  faltaNaConta,
  GRUPOS,
  NOME_DO_GRUPO,
  proximoCodigo,
  detalheDaClassificacao,
  tituloDaClassificacao,
  detalheDaContaAlterada,
  tituloDaContaAlterada,
  detalheDaContaCriada,
  detalheDaValidacao,
  type ValoresDaConta,
} from './financeiro';

/** Uma linha da classificação: a regra geral de uma origem, ou a exceção de um centro no indireto. */
interface LinhaDaClassificacao {
  readonly id: string;
  readonly origem: DescricaoDaOrigem;
  readonly centro: { readonly id: string; readonly codigo: string; readonly nome: string } | null;
  readonly classificacao: ClassificacaoDoPlano | null;
}

type Classificando = { readonly origem: Origem; readonly centroCustoId: string | null; readonly contaAtual: string | null };

/**
 * FERRAMENTA 4 (e a 2) DO MÓDULO 15 — o plano de contas gerencial da empresa,
 * a classificação de cada origem numa conta, e a validação da contabilidade.
 *
 * O plano é DADO da empresa: nasce com as contas padrão e a empresa ajusta. A
 * origem (o combustível, a receita da medição) é do sistema, que sabe lê-la do
 * módulo dela; a conta em que ela aparece é da empresa. Mudar a conta de uma
 * origem muda a linha em toda DRE consultada — por isso o motivo.
 */
export function PlanoDeContas({
  gere,
  criandoConta,
  aoFecharCriacao,
}: {
  readonly gere: boolean;
  readonly criandoConta: boolean;
  readonly aoFecharCriacao: () => void;
}) {
  const plano = useQuery({ queryKey: ['financeiro', 'plano'], queryFn: apiDoFinanceiro.plano, meta: { oQueLia: 'o plano de contas' } });
  const [editando, setEditando] = useState<ContaDoPlano | null>(null);
  const [classificando, setClassificando] = useState<Classificando | null>(null);
  const [validando, setValidando] = useState(false);
  const p = plano.data;

  const linhasDaClassificacao: LinhaDaClassificacao[] = p
    ? p.origens.flatMap((origem): LinhaDaClassificacao[] => {
      const geral = p.classificacoes.find((k) => k.origem === origem.origem && k.centroCustoId === null) ?? null;
      const excecoes = p.classificacoes
        .filter((k) => k.origem === origem.origem && k.centroCustoId !== null)
        .map((k) => ({
          id: k.id,
          origem,
          centro: { id: k.centroCustoId as string, codigo: k.centroCodigo ?? '', nome: k.centroNome ?? '' },
          classificacao: k,
        }));
      return [{ id: origem.origem, origem, centro: null, classificacao: geral }, ...excecoes];
    })
    : [];

  return (
    <>
      <Validacao plano={p} carregando={plano.isPending} gere={gere} aoValidar={() => { setValidando(true); }} />

      <Tabela
        rotulo="Contas do plano gerencial"
        colunas={colunasDasContas(p)}
        linhas={p?.contas ?? []}
        chaveDaLinha={(c) => c.id}
        carregando={plano.isPending}
        alturaDupla
        {...(gere
          ? {
              acoes: (c: ContaDoPlano) => (
                <MaisAcoes
                  acoes={[
                    { rotulo: 'Editar conta', aoEscolher: () => { setEditando(c); } },
                  ]}
                />
              ),
            }
          : {})}
        erro={plano.isError ? <ErroDaLista oQue="O plano de contas" erro={plano.error} aoTentarDeNovo={() => void plano.refetch()} /> : null}
        vazio={
          <EstadoVazio
            titulo="O plano de contas está vazio"
            descricao="Toda empresa nasce com o plano padrão. Se ele sumiu, peça ao suporte para recriá-lo; enquanto isso, a DRE mostra cada origem como “sem conta”."
          />
        }
        rodape={<RodapeDeContagem total={p?.contas.length ?? 0} />}
      />

      <Tabela
        rotulo="Em que conta cada origem aparece"
        colunas={COLUNAS_DA_CLASSIFICACAO}
        linhas={linhasDaClassificacao}
        chaveDaLinha={(l) => l.id}
        carregando={plano.isPending}
        alturaDupla
        {...(gere
          ? {
              acoes: (l: LinhaDaClassificacao) => (
                <MaisAcoes
                  acoes={[
                    {
                      rotulo: 'Mudar conta',
                      aoEscolher: () => {
                        setClassificando({ origem: l.origem.origem, centroCustoId: l.centro?.id ?? null, contaAtual: l.classificacao?.contaId ?? null });
                      },
                    },
                    ...(l.origem.origem === 'custo.indireto' && l.centro === null
                      ? [{ rotulo: 'Classificar um centro', aoEscolher: () => { setClassificando({ origem: 'custo.indireto', centroCustoId: '', contaAtual: null }); } }]
                      : []),
                    ...(l.centro
                      ? [{ rotulo: 'Voltar à regra geral', aoEscolher: () => { setClassificando({ origem: l.origem.origem, centroCustoId: l.centro?.id ?? null, contaAtual: null }); } }]
                      : []),
                  ]}
                />
              ),
            }
          : {})}
        erro={plano.isError ? <ErroDaLista oQue="A classificação das origens" erro={plano.error} aoTentarDeNovo={() => void plano.refetch()} /> : null}
        vazio={<EstadoVazio titulo="Nenhuma origem" descricao="As origens vêm dos módulos de custo e receita. Recarregue a tela; se continuar vazio, avise o suporte." />}
        rodape={<RodapeDeContagem total={linhasDaClassificacao.length} />}
      />

      {p && gere ? (
        <>
          <JanelaDaConta
            key={editando?.id ?? (criandoConta ? 'nova' : 'fechada')}
            plano={p}
            conta={editando}
            aberta={criandoConta || editando !== null}
            aoFechar={() => {
              setEditando(null);
              aoFecharCriacao();
            }}
          />
          <JanelaDaClassificacao
            key={classificando ? `${classificando.origem}-${classificando.centroCustoId ?? 'geral'}-${classificando.contaAtual ?? ''}` : 'fechada'}
            plano={p}
            alvo={classificando}
            aoFechar={() => {
              setClassificando(null);
            }}
          />
          <JanelaDaValidacao
            ultima={p?.validacao ?? null}
            aberta={validando}
            aoFechar={() => {
              setValidando(false);
            }}
          />
        </>
      ) : null}
    </>
  );
}

function colunasDasContas(p: Plano | undefined): readonly Coluna<ContaDoPlano>[] {
  const origensDa = (contaId: string) =>
    (p?.classificacoes ?? [])
      .filter((k) => k.contaId === contaId)
      .map((k) => `${p?.origens.find((o) => o.origem === k.origem)?.rotulo ?? k.origem}${k.centroCodigo ? ` (${k.centroCodigo})` : ''}`);
  return [
    { chave: 'codigo', rotulo: 'Código', celula: (c) => <span className="codigo text-tinta">{c.codigo}</span> },
    {
      chave: 'nome',
      rotulo: 'Conta',
      prioridade: 'chave',
      celula: (c) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{c.nome}</span>
          <TextoDaCelula texto={NOME_DO_GRUPO[c.grupo]} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'contabil',
      rotulo: 'Conta contábil',
      celula: (c) => (c.contaContabil ? <span className="codigo text-tinta">{c.contaContabil}</span> : <span className="txt-apoio text-tinta-secundaria">sem de-para</span>),
    },
    {
      chave: 'origens',
      rotulo: 'Origens',
      prioridade: 'secundaria',
      celula: (c) => {
        const lista = origensDa(c.id);
        return <TextoDaCelula texto={lista.length ? lista.join(', ') : 'nenhuma origem nesta conta'} classe={`txt-apoio ${lista.length ? 'text-tinta' : 'text-tinta-secundaria'}`} linhas={2} />;
      },
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      celula: (c) => <span className={`txt-corpo ${c.ativo ? 'text-tinta' : 'text-tinta-secundaria'}`}>{c.ativo ? 'Em uso' : 'Desativada'}</span>,
    },
  ];
}

const COLUNAS_DA_CLASSIFICACAO: readonly Coluna<LinhaDaClassificacao>[] = [
  {
    chave: 'origem',
    rotulo: 'Origem',
    prioridade: 'chave',
    celula: (l) => (
      <span className="flex min-w-0 flex-col">
        <span className={`txt-corpo text-tinta ${l.centro ? 'pl-4' : ''}`}>{l.centro ? `Do centro ${l.centro.codigo} · ${l.centro.nome}` : l.origem.rotulo}</span>
        <TextoDaCelula texto={l.centro ? 'Exceção do custo indireto' : `Módulo ${l.origem.modulo}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </span>
    ),
  },
  {
    chave: 'conta',
    rotulo: 'Conta na DRE',
    celula: (l) =>
      l.classificacao ? (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{l.classificacao.contaCodigo}</span>
          <TextoDaCelula texto={l.classificacao.contaNome} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ) : (
        <span className="txt-apoio text-atencao-texto">Sem conta: aparece como “sem conta” na DRE</span>
      ),
  },
  {
    chave: 'deOndeVem',
    rotulo: 'De onde vem o número',
    prioridade: 'secundaria',
    celula: (l) => <TextoDaCelula texto={l.origem.deOndeVem} classe="txt-apoio text-tinta-secundaria" linhas={2} />,
  },
];

/** A validação com a contabilidade: quando, quem, e o que mudou depois. */
function Validacao({ plano, carregando, gere, aoValidar }: { readonly plano: Plano | undefined; readonly carregando: boolean; readonly gere: boolean; readonly aoValidar: () => void }) {
  const base = useId();
  const v = plano?.validacao ?? null;
  return (
    <section className="cartao flex flex-col gap-3 p-6" aria-labelledby={`${base}-titulo`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 max-w-[80ch] flex-col gap-1">
          <h2 id={`${base}-titulo`} className="txt-secao text-tinta">
            Validação com a contabilidade
          </h2>
          {carregando ? (
            <Carregando oQue="Carregando a validação do plano">
              <Barra className="my-1 h-3 w-80" />
            </Carregando>
          ) : v ? (
            <p className="txt-apoio text-pretty text-tinta">{`Validado em ${formatarDia(v.validadoEm)} por ${v.contador}${v.registradoPor ? `, registrado por ${v.registradoPor}` : ''}.`}</p>
          ) : (
            <p className="flex items-start gap-2 txt-apoio text-pretty text-atencao-texto">
              <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              O plano ainda não foi validado com a contabilidade: a DRE é gerencial e pode não fechar com o balancete. Leve o plano ao contador e registre a validação.
            </p>
          )}
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            A conta contábil de cada linha é o de-para com o plano do ERP: é por ela que a integração do módulo 04 vai ligar a DRE à escrituração.
          </p>
        </div>
        {gere ? (
          <Botao nivel="secundario" onClick={aoValidar}>
            <FileCheck aria-hidden="true" className="size-4" />
            Registrar validação
          </Botao>
        ) : null}
      </div>
      {v && v.mudancasDesde.length > 0 ? (
        <div className="bloco-rebaixado flex flex-col gap-1 rounded-controle p-4">
          <span className="txt-corpo text-tinta">{`Depois da validação, ${String(v.mudancasDesde.length)} ${v.mudancasDesde.length === 1 ? 'mudança que a contabilidade ainda não viu' : 'mudanças que a contabilidade ainda não viu'}:`}</span>
          <ul className="txt-apoio list-disc pl-5 text-tinta-secundaria">
            {v.mudancasDesde.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      ) : v ? (
        <p className="txt-apoio text-tinta-secundaria">Nada mudou no plano depois da validação.</p>
      ) : null}
    </section>
  );
}

/* ============================================================ as janelas */

function JanelaDaConta({ plano, conta, aberta, aoFechar }: { readonly plano: Plano; readonly conta: ContaDoPlano | null; readonly aberta: boolean; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [v, setV] = useState<ValoresDaConta>(() =>
    conta
      ? { codigo: conta.codigo, nome: conta.nome, grupo: conta.grupo, contaContabil: conta.contaContabil ?? '' }
      : { codigo: proximoCodigo(plano.contas, 'custo_indireto'), nome: '', grupo: 'custo_indireto', contaContabil: '' },
  );
  const [ativo, setAtivo] = useState(conta?.ativo ?? true);
  /* A versao de quando o formulario abriu (robos de 20/10/2026): o servidor
     recusa o formulario velho em vez de gravar por cima de outra pessoa. */
  const [versao] = useState<string | null>(() => conta?.versao ?? null);
  const [motivo, setMotivo] = useState('');
  const [tentou, setTentou] = useState(false);
  const falta = faltaNaConta(v, plano.contas, conta?.id ?? null);
  const desativando = conta !== null && conta.ativo && !ativo;
  const faltaMotivo = desativando ? faltaNoMotivo(motivo) : null;
  const classificada = conta !== null && plano.classificacoes.some((k) => k.contaId === conta.id);
  const recarregar = () => cliente.invalidateQueries({ queryKey: ['financeiro'] });

  const criar = useMutation({
    mutationFn: (valores: ValoresDaConta) => apiDoFinanceiro.criarConta(corpoDaConta(valores)),
    meta: { retorno: retornoDe<{ codigo: string; nome: string; grupo: Grupo }>((r) => ({ titulo: 'Conta do plano gerencial criada', ...detalheDaContaCriada(r) })) },
    onSuccess: async () => {
      aoFechar();
      await recarregar();
    },
  });
  const alterar = useMutation({
    mutationFn: (p: { valores: ValoresDaConta; ativo: boolean; motivo: string }) =>
      apiDoFinanceiro.alterarConta(conta?.id ?? '', { ...corpoDaConta(p.valores), ativo: p.ativo, ...(p.motivo.trim() ? { motivo: p.motivo } : {}), versao }),
    meta: {
      retorno: retornoDe<{ codigo: string; nome: string; ativo: boolean; antes: { ativo: boolean; codigo: string } }, { motivo: string }>((r, p) => ({
        titulo: tituloDaContaAlterada(r),
        ...detalheDaContaAlterada(r, p.motivo),
      })),
    },
    onSuccess: async () => {
      aoFechar();
      await recarregar();
    },
    /* Recusada por versao velha: o plano rele, e reabrir mostra a conta de agora. */
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void recarregar();
    },
  });
  const gravando = criar.isPending || alterar.isPending;
  const recusa = criar.error ?? alterar.error;
  const erro = (campo: keyof ValoresDaConta) => (tentou ? (falta[campo] ?? null) : null);

  return (
    <Janela
      titulo={conta ? `Editar a conta ${conta.codigo}` : 'Criar conta no plano gerencial'}
      descricao={conta ? 'O grupo não muda: ele é o das origens classificadas na conta.' : 'A conta aparece na DRE quando uma origem for classificada nela.'}
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={gravando}
            onClick={() => {
              setTentou(true);
              if (Object.keys(falta).length > 0 || faltaMotivo) return;
              if (conta) alterar.mutate({ valores: v, ativo, motivo });
              else criar.mutate(v);
            }}
          >
            {gravando ? 'Gravando…' : conta ? 'Salvar conta' : 'Criar conta'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {recusa ? (
          <p role="alert" className="txt-apoio text-parada-texto">
            {`${explicarFalha(recusa).erro}. ${explicarFalha(recusa).comoResolver}`}
          </p>
        ) : null}
        <CampoSelecao
          rotulo="Grupo na DRE"
          valor={v.grupo}
          aoMudar={(g) => {
            if (conta) return;
            setV({ ...v, grupo: g as Grupo, codigo: proximoCodigo(plano.contas, g as Grupo) });
          }}
          opcoes={GRUPOS.map((g) => ({ valor: g, rotulo: plano.grupos.find((x) => x.grupo === g)?.rotulo ?? NOME_DO_GRUPO[g] }))}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[120px_1fr]">
          <CampoTexto rotulo="Código" valor={v.codigo} aoMudar={(codigo) => { setV({ ...v, codigo }); }} validar={() => erro('codigo')} maxLength={12} />
          <CampoTexto rotulo="Nome" valor={v.nome} aoMudar={(nome) => { setV({ ...v, nome }); }} placeholder="Seguros da frota" validar={() => erro('nome')} maxLength={80} />
        </div>
        <CampoTexto
          rotulo="Conta contábil"
          opcional
          valor={v.contaContabil}
          aoMudar={(contaContabil) => { setV({ ...v, contaContabil }); }}
          placeholder="4.1.02.007"
          ajuda="O código da conta no plano do ERP. É o de-para que a integração vai usar."
          maxLength={40}
        />
        {tentou && (falta.codigo || falta.nome) ? (
          <p role="alert" className="txt-apoio text-parada-texto">{falta.codigo ?? falta.nome}</p>
        ) : null}
        {conta ? (
          <CampoSelecao
            rotulo="Situação"
            valor={ativo ? 'ativa' : 'desativada'}
            aoMudar={(s) => { setAtivo(s === 'ativa'); }}
            opcoes={[{ valor: 'ativa', rotulo: 'Em uso' }, { valor: 'desativada', rotulo: 'Desativada' }]}
          />
        ) : null}
        {desativando && classificada ? (
          <p className="txt-apoio text-pretty text-atencao-texto">
            Esta conta tem origem classificada nela. Mude a conta dessas origens antes; desativada, o valor delas ficaria sem linha.
          </p>
        ) : null}
        {desativando ? (
          <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={EXEMPLO_DO_MOTIVO['conta_gerencial.desativar']} />
        ) : null}
      </div>
    </Janela>
  );
}

function JanelaDaClassificacao({ plano, alvo, aoFechar }: { readonly plano: Plano; readonly alvo: Classificando | null; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const origem = plano.origens.find((o) => o.origem === alvo?.origem);
  const voltando = alvo !== null && alvo.centroCustoId !== null && alvo.centroCustoId !== '' && alvo.contaAtual === null;
  const [centro, setCentro] = useState(alvo?.centroCustoId ?? '');
  const [contaId, setContaId] = useState(alvo?.contaAtual ?? '');
  const [motivo, setMotivo] = useState('');
  const contas = plano.contas.filter((c) => c.ativo && c.grupo === origem?.grupo);
  const escolhendoCentro = alvo?.centroCustoId === '';
  const centroDoAlvo = plano.centros.find((c) => c.id === (escolhendoCentro ? centro : alvo?.centroCustoId));
  const rotulo = origem ? `${origem.rotulo}${centroDoAlvo ? ` do centro ${centroDoAlvo.codigo}` : ''}` : '';
  const faltaMotivo = faltaNoMotivo(motivo);
  const pronto = !faltaMotivo && (voltando || contaId !== '') && (!escolhendoCentro || centro !== '');

  const classificar = useMutation({
    mutationFn: (p: { origem: Origem; centroCustoId: string | null; contaId: string | null; motivo: string; rotulo: string }) =>
      apiDoFinanceiro.classificar({ origem: p.origem, centroCustoId: p.centroCustoId, contaId: p.contaId, motivo: p.motivo }),
    meta: {
      retorno: retornoDe<{ antes: string | null; depois: { codigo: string; nome: string } | null }, { motivo: string; rotulo: string }>((r, p) => ({
        titulo: tituloDaClassificacao(r),
        ...detalheDaClassificacao(r, p.rotulo, p.motivo),
      })),
    },
    onSuccess: async () => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['financeiro'] });
    },
  });

  return (
    <Janela
      titulo={voltando ? 'Voltar à regra geral' : 'Em que conta esta origem aparece'}
      descricao="Muda a linha em que o valor aparece em toda DRE consultada, inclusive nos meses que já passaram. O total não muda."
      aberta={alvo !== null}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={!pronto || classificar.isPending}
            onClick={() => {
              if (!alvo || !pronto) return;
              classificar.mutate({
                origem: alvo.origem,
                centroCustoId: escolhendoCentro ? centro : alvo.centroCustoId,
                contaId: voltando ? null : contaId,
                motivo,
                rotulo,
              });
            }}
          >
            {classificar.isPending ? 'Gravando…' : voltando ? 'Voltar à regra geral' : 'Classificar origem'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {classificar.isError ? (
          <p role="alert" className="txt-apoio text-parada-texto">
            {`${explicarFalha(classificar.error).erro}. ${explicarFalha(classificar.error).comoResolver}`}
          </p>
        ) : null}
        <p className="txt-corpo text-tinta">{rotulo || 'Escolha o centro de custo'}</p>
        {escolhendoCentro ? (
          <CampoSelecao
            rotulo="Centro de custo"
            valor={centro}
            aoMudar={setCentro}
            rotuloVazio="Escolha o centro"
            opcoes={plano.centros.filter((c) => c.ativo).map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.nome}` }))}
          />
        ) : null}
        {voltando ? (
          <p className="txt-apoio text-pretty text-tinta-secundaria">O custo indireto deste centro volta para a conta da regra geral do custo indireto.</p>
        ) : (
          <CampoSelecao
            rotulo="Conta"
            valor={contaId}
            aoMudar={setContaId}
            rotuloVazio="Escolha a conta"
            opcoes={contas.map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.nome}` }))}
          />
        )}
        {!voltando && contas.length === 0 ? (
          <p className="txt-apoio text-atencao-texto">Nenhuma conta em uso neste grupo. Crie uma conta do grupo antes de classificar.</p>
        ) : null}
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={EXEMPLO_DO_MOTIVO['classificacao.trocar']} />
      </div>
    </Janela>
  );
}

/** Hoje no fuso da operação (`diaDaOperacao`), e não no fuso do aparelho. */
function hojeIso(): string {
  return diaDaOperacao();
}

function JanelaDaValidacao({
  ultima,
  aberta,
  aoFechar,
}: {
  /** A validação em vigor: o contador dela é quem costuma validar de novo (31/10/2026). */
  readonly ultima: { readonly validadoEm: string; readonly contador: string } | null;
  readonly aberta: boolean;
  readonly aoFechar: () => void;
}) {
  const cliente = useQueryClient();
  const [quando, setQuando] = useState(hojeIso);
  /* `null` = o contador da última validação; o texto é o que a pessoa escreveu. */
  const [contadorDigitado, setContador] = useState<string | null>(null);
  const contador = contadorDigitado ?? ultima?.contador ?? '';
  const [observacao, setObservacao] = useState('');
  const faltaQuem = contador.trim().length < 3 ? 'Escreva quem validou, com o registro, por exemplo "Maria Souza, CRC MT-012345".' : null;
  const faltaData = !/^\d{4}-\d{2}-\d{2}$/.test(quando) || quando > hojeIso() ? 'Escolha a data da validação, de hoje ou de antes.' : null;
  const validar = useMutation({
    mutationFn: () => apiDoFinanceiro.validar({ validadoEm: quando, contador, observacao }),
    meta: { retorno: retornoDe<{ validadoEm: string; contador: string; contas: number }>((r) => ({ titulo: 'Validação da contabilidade registrada', ...detalheDaValidacao(r) })) },
    onSuccess: async () => {
      aoFechar();
      setContador(null);
      setObservacao('');
      await cliente.invalidateQueries({ queryKey: ['financeiro', 'plano'] });
    },
  });
  return (
    <Janela
      titulo="Registrar a validação da contabilidade"
      descricao="Guarda o plano como está hoje. O que mudar depois aparece na tela, para levar ao contador de novo."
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={faltaQuem !== null || faltaData !== null || validar.isPending}
            onClick={() => {
              validar.mutate();
            }}
          >
            {validar.isPending ? 'Registrando…' : 'Registrar validação'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {validar.isError ? (
          <p role="alert" className="txt-apoio text-parada-texto">
            {`${explicarFalha(validar.error).erro}. ${explicarFalha(validar.error).comoResolver}`}
          </p>
        ) : null}
        <CampoDeData rotulo="Validado em" valor={quando} aoMudar={setQuando} max={hojeIso()} ajuda="A data em que a contabilidade validou, até hoje." erro={faltaData} />
        <CampoTexto
          rotulo="Quem validou"
          valor={contador}
          aoMudar={setContador}
          placeholder="Maria Souza, CRC MT-012345"
          validar={() => faltaQuem}
          maxLength={120}
          {...(contadorDigitado === null && ultima ? { ajuda: `Quem validou da última vez, em ${formatarDia(ultima.validadoEm)}. Troque se foi outra pessoa.` } : {})}
        />
        <CampoTexto rotulo="Observação" opcional valor={observacao} aoMudar={setObservacao} maxLength={400} />
      </div>
    </Janela>
  );
}
